// @ts-check
import icProductionOrdersStatus from '@/static/icons/ic_estado_blue.svg';
import icManageProductionLines from '@/static/icons/ic_lineas_blue.svg';
import icMore from '@/static/icons/ic_more.svg';

import icPalletizing from '@/static/icons/ic_paletizado_blue.svg';
import icMultiplePalletizing from '@/static/icons/ic_paletizadomultiple_blue.svg';
import icExit from '@/static/icons/ic_salir_conFondoAzul.svg';
import icNext from '@/static/img/ic_next.svg';
import { ModuleProcessMappings } from '@erpagroapp/common';
import { useSettingRef } from '@erpagroapp/settings';
import { computed, ref } from 'vue';

/** @import { Ref } from 'vue' */

/**
 * @typedef {'palletizing' | 'multiplePalletizing' | 'manageProductionLines' |
 * 'productionOrdersStatus' | 'manageProductionOutputs'} SideNavbarItemSettingKeyConfigurable
 */

/** @typedef {SideNavbarItemSettingKeyConfigurable | 'next' | 'more' | 'back'} SideNavbarItemSettingKey */
/** @typedef {`show${Capitalize<SideNavbarItemSettingKeyConfigurable>}`} SideNavbarItemSettingName */
/** @typedef {'top' | 'bottom'} SideNavbarItemPosition */

/**
 * @typedef {object} SideNavbarItem
 * @property {string} title - The title of the item.
 * @property {SideNavbarItemSettingKey} key - A unique identificator of the item.
 * @property {SideNavbarItemSettingName} [settingName] - The name of the setting for the item.
 * @property {number} [processId] - The id of the item.
 * @property {string} [route] - The route of the item.
 * @property {string} [icon] - The icon class for the item. If not specified, a default icon will be used.
 * @property {SideNavbarItemPosition} position - The position of the item.
 * @property {boolean} [skipAccesibilityCheck=false] - Whether to skip the accessibility check.
 * @property {string} [width='26.3px'] - The width of the item.
 * @property {string} [height='36.5px'] - The height of the item.
 */

/** @type {Ref<SideNavbarItem[]>} */
const items = ref([
  {
    title: 'sideNavbar.options.palletizing',
    key: 'palletizing',
    settingName: 'showPalletizing',
    processId: ModuleProcessMappings.ProductionControl.Submodules.Palletized.Processes.Palletized.Id,
    route: '/production/traceability/palletizing/FrmPalletizingHome',
    icon: icPalletizing,
    position: 'top',
  },
  {
    title: 'sideNavbar.options.multiplePalletizing',
    key: 'multiplePalletizing',
    settingName: 'showMultiplePalletizing',
    processId: ModuleProcessMappings.ProductionControl.Submodules.Palletized.Processes.MultiplePalletized.Id,
    route: '/production/traceability/palletizing/FrmMultipointPalletizing',
    icon: icMultiplePalletizing,
    position: 'top',
  },
  {
    title: 'sideNavbar.options.manageProductionLines',
    key: 'manageProductionLines',
    settingName: 'showManageProductionLines',
    processId: ModuleProcessMappings.ProductionControl.Processes.ManageProductionLines.Id,
    route: '/production/traceability/manageProductionLines/FrmManageProductionLines',
    icon: icManageProductionLines,
    position: 'top',
  },
  {
    title: 'sideNavbar.options.productionOrdersStatus',
    key: 'productionOrdersStatus',
    settingName: 'showProductionOrdersStatus',
    processId: ModuleProcessMappings.ProductionControl.Processes.ProductionPlanningStatus.Id,
    route: '/production/roductionLines/productionLinesManagement/FrmProductionPlanningStatus',
    icon: icProductionOrdersStatus,
    position: 'top',
  },
  {
    title: 'sideNavbar.options.manageProductionOutputs',
    key: 'manageProductionOutputs',
    settingName: 'showManageProductionOutputs',
    processId: ModuleProcessMappings.ProductionControl.Processes.ManageProductionOutputs.Id,
    route: '/production/traceability/manageProductionOutputs/FrmManageProductionOutputs',
    icon: undefined, /** default */
    position: 'top',
  },
  {
    title: 'common.next',
    key: 'next',
    icon: icNext,
    position: 'bottom',
    skipAccesibilityCheck: true,
    width: '32px',
    height: '32px',
  },
  {
    title: 'common.more',
    key: 'more',
    icon: icMore,
    position: 'bottom',
    skipAccesibilityCheck: true,
    width: '48px',
    height: '32px',
  },
  {
    title: 'sideNavbar.options.back',
    key: 'back',
    icon: icExit,
    position: 'bottom',
    skipAccesibilityCheck: true,
    width: '40px',
    height: '40px',
  },
]);

export function useSideNavbar() {
  const configurableItems = computed(() => items.value.filter((item) => !!item.settingName));

  /** @type {Record<SideNavbarItemSettingKeyConfigurable, Ref<boolean>>} */
  const sideNavbarRefs = {
    palletizing: useSettingRef('BarraNavegacionLateral.MostrarPaletizado'),
    multiplePalletizing: useSettingRef('BarraNavegacionLateral.MostrarPaletizadoMultiple'),
    manageProductionLines: useSettingRef('BarraNavegacionLateral.MostrarGestionarLineasProduccion'),
    productionOrdersStatus: useSettingRef('BarraNavegacionLateral.MostrarEstadoPedidosProduccion'),
    manageProductionOutputs: useSettingRef('BarraNavegacionLateral.MostrarGestionarSalidasProduccion'),
  };

  return {
    items,
    configurableItems,
    sideNavbarRefs,
  };
}
